import React, { useState } from 'react';
import { X, Zap, Save } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentSettingsModal = ({ caseId, settings, onClose, onSave }) => {
  const [autoTriggerDocument, setAutoTriggerDocument] = useState(
    settings?.auto_trigger_rules?.DOCUMENT?.enabled || false
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/agents/settings/${caseId}`, {
        auto_trigger_rules: {
          DOCUMENT: {
            enabled: autoTriggerDocument,
            trigger_on: ['document_upload']
          }
        }
      });

      toast.success('Innstillinger lagret!');
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            Agent-innstillinger
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Auto-trigger</h4>
            <p className="text-sm text-gray-600 mb-4">
              Kjør agenter automatisk når visse hendelser skjer
            </p>

            {/* Document Agent Auto-Trigger */}
            <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                id="auto-document"
                checked={autoTriggerDocument}
                onChange={(e) => setAutoTriggerDocument(e.target.checked)}
                className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-gray-500"
              />
              <div className="flex-1">
                <label htmlFor="auto-document" className="font-medium text-gray-900 text-sm cursor-pointer">
                  DokumentAgent ved dokumentopplasting
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Kjører automatisk når et nytt dokument lastes opp til denne saken
                </p>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-gray-50 border border-indigo-200 rounded-lg p-3">
            <p className="text-xs text-indigo-900">
              <strong>Tips:</strong> Auto-trigger sparer tid og sikrer at AI alltid analyserer nye dokumenter.
              Du får fortsatt forslag du må godkjenne.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={saving}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            disabled={saving}
          >
            {saving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-spin" />
                Lagrer...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Lagre
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgentSettingsModal;
